import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

export const fetchWallet = createAsyncThunk('credits/fetchWallet', async ({ ownerId, ownerType }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/credits/wallet/balance/${ownerType}/${ownerId}`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchWalletById = createAsyncThunk('credits/fetchWalletById', async (walletId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/credits/wallet/${walletId}`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchTransfers = createAsyncThunk('credits/fetchTransfers', async ({ walletId, page = 1 }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/credits/transfer/wallet/${walletId}?page=${page}&limit=20`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchTransferStats = createAsyncThunk('credits/fetchTransferStats', async ({ ownerId, ownerType }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/credits/transfer/stats/${ownerType}/${ownerId}`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const createTransfer = createAsyncThunk('credits/createTransfer', async (transferData, { rejectWithValue }) => {
  try {
    const response = await api.post('/credits/transfer', transferData);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchPacks = createAsyncThunk('credits/fetchPacks', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/credits/pack');
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const purchasePack = createAsyncThunk('credits/purchasePack', async ({ packId, ownerId, ownerType, paymentDetails }, { rejectWithValue }) => {
  try {
    const response = await api.post(`/credits/pack/${packId}/purchase`, { ownerId, ownerType, paymentDetails });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchTransactions = createAsyncThunk('credits/fetchTransactions', async ({ walletId, page = 1, type }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams({ page, limit: 50 });
    if (type) params.append('type', type);
    const response = await api.get(`/credits/transaction/wallet/${walletId}?${params}`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const fetchPendingApprovals = createAsyncThunk('credits/fetchPendingApprovals', async ({ ownerId, ownerType, page = 1 }, { rejectWithValue }) => {
  try {
    const response = await api.get(`/credits/transfer/pending/${ownerType}/${ownerId}?page=${page}&limit=20`);
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const approveTransfer = createAsyncThunk('credits/approveTransfer', async ({ transferId, approverId }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/credits/transfer/${transferId}/approve`, { approverId });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

export const rejectTransfer = createAsyncThunk('credits/rejectTransfer', async ({ transferId, rejectedBy, reason }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/credits/transfer/${transferId}/reject`, { rejectedBy, reason });
    return response.data.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: error.message });
  }
});

const creditsSlice = createSlice({
  name: 'credits',
  initialState: {
    wallet: null,
    transfers: [],
    transactions: [],
    packs: [],
    transferStats: null,
    pendingApprovals: [],
    pagination: { page: 1, pages: 1, total: 0 },
    txPagination: { page: 1, pages: 1, total: 0 },
    isLoading: false,
    error: null,
    successMessage: null
  },
  reducers: {
    clearCreditsError: (state) => { state.error = null; },
    clearCreditsSuccess: (state) => { state.successMessage = null; },
    resetCreditsState: (state) => {
      state.wallet = null;
      state.transfers = [];
      state.transactions = [];
      state.transferStats = null;
      state.pendingApprovals = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWallet.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchWallet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wallet = action.payload;
      })
      .addCase(fetchWallet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to load wallet';
      })
      .addCase(fetchWalletById.fulfilled, (state, action) => {
        state.wallet = action.payload;
      })
      .addCase(fetchTransfers.pending, (state) => { state.isLoading = true; })
      .addCase(fetchTransfers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transfers = action.payload.transfers || [];
        state.pagination = { page: action.payload.page, pages: action.payload.pages, total: action.payload.total };
      })
      .addCase(fetchTransferStats.fulfilled, (state, action) => {
        state.transferStats = action.payload;
      })
      .addCase(createTransfer.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(createTransfer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transfers.unshift(action.payload);
        state.successMessage = 'Transfer initiated successfully';
      })
      .addCase(createTransfer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Transfer failed';
      })
      .addCase(fetchPacks.pending, (state) => { state.isLoading = true; })
      .addCase(fetchPacks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.packs = action.payload;
      })
      .addCase(purchasePack.fulfilled, (state, action) => {
        if (action.payload?.wallet) state.wallet = action.payload.wallet;
        state.successMessage = 'Pack purchased successfully';
      })
      .addCase(purchasePack.rejected, (state, action) => {
        state.error = action.payload?.message || 'Purchase failed';
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.transactions = action.payload.transactions || [];
        state.txPagination = { page: action.payload.page, pages: action.payload.pages, total: action.payload.total };
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.pendingApprovals = action.payload.transfers || [];
      })
      .addCase(approveTransfer.fulfilled, (state, action) => {
        state.pendingApprovals = state.pendingApprovals.filter(t => t._id !== action.payload._id);
        state.successMessage = 'Transfer approved';
      })
      .addCase(rejectTransfer.fulfilled, (state, action) => {
        state.pendingApprovals = state.pendingApprovals.filter(t => t._id !== action.payload._id);
        state.successMessage = 'Transfer rejected';
      });
  }
});

export const { clearCreditsError, clearCreditsSuccess, resetCreditsState } = creditsSlice.actions;
export default creditsSlice.reducer;